import React from 'react';
import { createSafeHtml } from '../../utils/sanitize';

interface SafeHtmlProps {
  html: string | null | undefined;
  
  allowBasicFormatting?: boolean;
  
  as?: keyof JSX.IntrinsicElements;
  
  /**
   * Additional CSS class names
   */
  className?: string;
  
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

/**
 * SafeHtml component that safely renders HTML content by sanitizing it first
 * This prevents XSS attacks by removing dangerous HTML tags and attributes
 * 
 * @example
 * // Strict mode - strips all HTML
 * <SafeHtml html={userGeneratedContent} />
 * 
 * @example
 * // Allow basic formatting
 * <SafeHtml 
 *   html={userBio} 
 *   allowBasicFormatting={true}
 *   className="user-bio"
 * />
 * 
 * @example
 * // Use different container element
 * <SafeHtml 
 *   html={content} 
 *   as="span"
 *   style={{ color: 'blue' }}
 * />
 */
export const SafeHtml: React.FC<SafeHtmlProps> = ({
  html,
  allowBasicFormatting = false,
  as: Component = 'div',
  className,
  style,
}) => {
  return React.createElement(Component, {
    className,
    style,
    dangerouslySetInnerHTML: createSafeHtml(html, allowBasicFormatting),
  });
};

export default SafeHtml;

